import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "../../api/auth_api";

const CandlestickChartBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let candles = [];
        let maData = [];
        let volumeData = [];
        let signals = [];
        let scrollOffset = 0;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        const generateCandle = (prevClose) => {
            const open = prevClose + (Math.random() - 0.5) * 15;
            const close = open + (Math.random() - 0.5) * 20;
            const high = Math.max(open, close) + Math.random() * 10;
            const low = Math.min(open, close) - Math.random() * 10;
            const volume = Math.random() * 50 + 20;
            
            return { open, high, low, close, volume };
        };

        let lastClose = 300;
        const candleCount = Math.ceil(canvas.width / 25) + 10;
        
        for (let i = 0; i < candleCount; i++) {
            const candle = generateCandle(lastClose);
            candles.push(candle);
            volumeData.push(candle.volume);
            lastClose = candle.close;
            
            if (i >= 19) {
                const sum = candles.slice(i - 19, i + 1).reduce((acc, c) => acc + c.close, 0);
                maData.push(sum / 20);
            } else {
                maData.push(null);
            }
            
            if (i > 20 && Math.random() > 0.92) {
                signals.push({
                    index: i,
                    type: Math.random() > 0.5 ? 'buy' : 'sell',
                    price: candle.close
                });
            }
        }

        const drawCandle = (x, candle, width) => {
            const bullish = candle.close > candle.open;
            const bodyTop = Math.min(candle.open, candle.close);
            const bodyBottom = Math.max(candle.open, candle.close);
            const bodyHeight = Math.abs(candle.close - candle.open);

            ctx.strokeStyle = bullish ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x + width / 2, candle.high);
            ctx.lineTo(x + width / 2, candle.low);
            ctx.stroke();

            ctx.fillStyle = bullish ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            ctx.fillRect(x, bodyTop, width - 2, Math.max(bodyHeight, 2));
        };

        const drawVolume = (x, volume, width, maxVolume) => {
            const volumeHeight = (volume / maxVolume) * 80;
            ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
            ctx.fillRect(x, canvas.height - volumeHeight - 10, width - 2, volumeHeight);
        };

        const drawSignal = (x, y, type) => {
            ctx.save();
            ctx.fillStyle = type === 'buy' ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
            ctx.strokeStyle = type === 'buy' ? 'rgba(34, 197, 94, 1)' : 'rgba(239, 68, 68, 1)';
            ctx.lineWidth = 2;
            
            ctx.beginPath();
            if (type === 'buy') {
                ctx.moveTo(x, y + 15);
                ctx.lineTo(x - 8, y + 5);
                ctx.lineTo(x + 8, y + 5);
            } else {
                ctx.moveTo(x, y - 15);
                ctx.lineTo(x - 8, y - 5);
                ctx.lineTo(x + 8, y - 5);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            
            ctx.fillStyle = 'white';
            ctx.font = 'bold 10px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(type.toUpperCase(), x, y + (type === 'buy' ? 30 : -20));
            ctx.restore();
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const candleWidth = 20;
            const spacing = 25;
            const chartHeight = canvas.height - 120;
            
            const visibleCandles = candles.slice(0, candleCount);
            const allPrices = visibleCandles.flatMap(c => [c.high, c.low]);
            const minPrice = Math.min(...allPrices) - 20;
            const maxPrice = Math.max(...allPrices) + 20;
            const priceRange = maxPrice - minPrice;
            
            const scaleY = (price) => {
                return 40 + ((maxPrice - price) / priceRange) * (chartHeight - 80);
            };

            const maxVolume = Math.max(...volumeData);

            ctx.strokeStyle = 'rgba(100, 116, 139, 0.1)';
            ctx.lineWidth = 1;
            for (let i = 0; i < 5; i++) {
                const y = 40 + (chartHeight - 80) * (i / 4);
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            ctx.strokeStyle = 'rgba(234, 179, 8, 0.6)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            let maStarted = false;
            
            candles.forEach((candle, i) => {
                if (maData[i] !== null) {
                    const x = (i * spacing - scrollOffset) + candleWidth / 2;
                    const y = scaleY(maData[i]);
                    
                    if (x > -spacing && x < canvas.width + spacing) {
                        if (!maStarted) {
                            ctx.moveTo(x, y);
                            maStarted = true;
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                }
            });
            ctx.stroke();

            candles.forEach((candle, i) => {
                const x = i * spacing - scrollOffset;
                
                if (x > -spacing && x < canvas.width + spacing) {
                    const scaledCandle = {
                        open: scaleY(candle.open),
                        high: scaleY(candle.high),
                        low: scaleY(candle.low),
                        close: scaleY(candle.close)
                    };
                    
                    drawCandle(x, scaledCandle, candleWidth);
                    drawVolume(x, volumeData[i], candleWidth, maxVolume);
                }
            });

            signals.forEach(signal => {
                const x = signal.index * spacing - scrollOffset + candleWidth / 2;
                const y = scaleY(signal.price);
                
                if (x > 0 && x < canvas.width) {
                    drawSignal(x, y, signal.type);
                }
            });

            scrollOffset += 0.5;
            
            if (scrollOffset >= spacing) {
                scrollOffset -= spacing;
                
                candles.shift();
                maData.shift();
                volumeData.shift();
                signals = signals.map(s => ({ ...s, index: s.index - 1 })).filter(s => s.index >= 0);
                
                const newCandle = generateCandle(candles[candles.length - 1].close);
                candles.push(newCandle);
                volumeData.push(newCandle.volume);
                
                const sum = candles.slice(-20).reduce((acc, c) => acc + c.close, 0);
                maData.push(sum / 20);
                
                if (Math.random() > 0.95) {
                    signals.push({
                        index: candles.length - 1,
                        type: Math.random() > 0.5 ? 'buy' : 'sell',
                        price: newCandle.close
                    });
                }
            }

            ctx.fillStyle = 'rgba(30, 41, 59, 0.8)';
            ctx.fillRect(10, 10, 250, 60);
            ctx.strokeStyle = 'rgba(100, 116, 139, 0.5)';
            ctx.strokeRect(10, 10, 250, 60);
            
            ctx.font = '12px Arial';
            ctx.fillStyle = 'rgba(148, 163, 184, 1)';
            ctx.textAlign = 'left';
            ctx.fillText('Live Market Simulation', 20, 30);
            
            ctx.fillStyle = 'rgba(34, 197, 94, 1)';
            ctx.fillText('● Green = Bullish', 20, 50);
            ctx.fillStyle = 'rgba(239, 68, 68, 1)';
            ctx.fillText('● Red = Bearish', 150, 50);

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.5 }}
        />
    );
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    const res = await forgotPassword({
      user_email: email,
    });

    if (res.success) {
      setSuccess(
        res.data?.response_message ||
          "If an account exists for this email, a password reset link has been sent."
      );
    } else {
      setError(res.error || "Unable to process request. Please try again.");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 bg-[#0F172A] overflow-hidden">
      {/* Animated Candlestick Chart Background */}
      <CandlestickChartBackground />

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

      {/* Forgot Password Box */}
      <div className="relative w-full max-w-md z-10">
        <div className="bg-[#1E293B]/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 p-8 space-y-6">
          {/* Logo */}
          <div className="text-center">
            <h1 className="font-bold text-3xl text-blue-400 mb-2">
              FactorAlpha
            </h1>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto rounded-full" />
          </div>

          {/* Heading */}
          <div className="text-center">
            <p className="font-semibold text-2xl mb-2 text-white">
              Forgot Password
            </p>
            <p className="text-sm text-slate-400">
              Enter your email to receive a password reset link
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-sm border border-red-500/50">
              {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="bg-green-500/20 text-green-400 p-3 rounded-lg text-sm border border-green-500/50">
              {success}
            </div>
          )}

          {/* FORM */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              required
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-slate-400 transition-all"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-white font-medium transition-all bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-blue-500/50"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          {/* Links */}
          <div className="text-sm text-center space-y-2 pt-4 border-t border-slate-700/50">
            <Link
              to="/login"
              className="font-medium block text-blue-400 hover:underline transition-colors"
            >
              Back to Login
            </Link>

            <div>
              <span className="text-slate-400">
                Don&apos;t have an account?{" "}
              </span>
              <Link
                to="/signup"
                className="font-medium text-blue-400 hover:underline transition-colors"
              >
                Create New Account
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -top-4 -left-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
        <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl" />
      </div>
    </div>
  );
};

export default ForgotPassword;